import { useUserInfo } from "@/api/user";
import { AccountPopover } from "@/components/AccountPopover/AccountPopover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { PersonAvatar } from "@cohesive/ui-components";

/**
 * The identity avatar that survives collapse (DS left-pane spec §12,
 * decision 021): ONE avatar in the DOM, rendered as a sibling of the
 * docked pane and pinned to the exact spot the footer's avatar occupies —
 * footer px-3 + trigger px-1 = 16 left; footer py-2.5 + trigger py-1 = 14
 * bottom (the spec's bottom-3.5 left-4). The footer renders an empty
 * size-8 box in its place, so collapsing wipes the rail out from behind
 * the avatar rather than cross-fading a twin in — no movement, nothing to
 * align, no chrome of its own: the spec's bare round avatar, which also
 * keeps the account quick-nav reachable with the pane closed.
 */
export function PersistentRailAvatar() {
  const { data: userInfo } = useUserInfo();
  const user = userInfo?.user;

  return (
    <div className="pointer-events-none absolute bottom-3.5 left-4 z-40">
      <Tooltip>
        <AccountPopover popoverSide="right" popoverAlign="end">
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Account"
              className={cn(
                "pointer-events-auto relative block size-8 rounded-full outline-none",
                // This avatar sits on top of the footer row's avatar slot and
                // swallows the row's hover, so it answers for itself: a scrim on
                // hover, one rung darker while the menu is held open — the same
                // two steps the identity row takes.
                "after:absolute after:inset-0 after:rounded-full after:transition-colors after:content-['']",
                "hover:after:bg-[color-mix(in_oklab,var(--n-95)_8%,transparent)]",
                "aria-expanded:after:bg-[color-mix(in_oklab,var(--n-95)_16%,transparent)]",
                "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-2",
              )}
            >
              <PersonAvatar
                name={user?.name}
                email={user?.email}
                pictureUrl={user?.picture_url}
              />
            </button>
          </TooltipTrigger>
        </AccountPopover>
        {user?.email ? (
          <TooltipContent side="top" align="start">
            {user.email}
          </TooltipContent>
        ) : null}
      </Tooltip>
    </div>
  );
}
